// MinimalCommentsEnhanced.jsx - Enhanced discussion section with consistent UI
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  useTheme,
  alpha,
  Fade,
} from '@mui/material';
import {
  Send as SendIcon,
  Lock as PrivateIcon,
  Chat as DiscussionIcon,
  Person as PersonIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// Import stores
import useTaskStore from '../../../stores/taskStore';
import useAuthStore from '../../../stores/authStore';
import useNotificationStore from '../../../stores/notificationStore';

// Import mention components
import InlineMentionInput from '../../ui/FormFields/InlineMentionInput';
import MentionText from '../../ui/MentionText';

/**
 * Enhanced MinimalComments with consistent UI styling
 * Matches the design patterns of attachments, checklist, and subtasks sections
 */
const MinimalCommentsEnhanced = ({ taskId }) => {
  const theme = useTheme();
  const commentInputRef = useRef(null);
  const [comment, setComment] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredComment, setHoveredComment] = useState(null);
  
  // Stores
  const { currentTask, patchTask } = useTaskStore();
  const { user } = useAuthStore();
  const { showError } = useNotificationStore();
  
  // Get comments
  const comments = currentTask?.comments || [];
  const visibleComments = showAllComments ? comments : comments.slice(-3);
  const hiddenCount = comments.length - visibleComments.length;
  
  // Handle comment submission
  const handleSubmit = useCallback(async () => {
    if (!comment.trim()) return;
    
    setIsSubmitting(true);
    try {
      await patchTask(taskId, {
        comment: {
          text: comment.trim(),
          isInternal: isInternalNote,
        }
      });
      
      setComment('');
      setIsInternalNote(false);
    } catch (error) {
      showError('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  }, [comment, isInternalNote, patchTask, taskId, showError]);
  
  // Handle Cmd+Enter (or Ctrl+Enter) key submit directly on input
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && comment.trim()) {
      console.log('Keyboard submit triggered');
      e.preventDefault();
      handleSubmit();
    }
  }, [comment, handleSubmit]);
  
  // Format user initials
  const getInitials = (user) => {
    if (!user) return '?';
    // Handle both populated and non-populated user objects
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
    return initials || '?';
  };
  
  // Get user name safely
  const getUserName = (user) => {
    if (!user) return 'Unknown User';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown User';
  };
  
  // Format relative time
  const formatTime = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (error) {
      return 'Unknown time';
    }
  };
  
  return (
    <Box>
      {/* Container with consistent styling */}
      <Box sx={{
        p: 1.5,
        bgcolor: alpha(theme.palette.background.paper, 0.5),
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: comments.length > 0 ? 1.5 : 0,
          pb: comments.length > 0 ? 1 : 0,
          borderBottom: comments.length > 0 ? `1px solid ${alpha(theme.palette.divider, 0.06)}` : 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DiscussionIcon sx={{ 
              fontSize: 16, 
              color: theme.palette.text.secondary 
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Discussion
            </Typography>
            {comments.length > 0 && (
              <Chip
                label={comments.length}
                size="small"
                sx={{
                  height: 18,
                  minWidth: 18,
                  fontSize: '0.7rem',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  '& .MuiChip-label': {
                    px: 0.75
                  }
                }}
              />
            )}
          </Box>
        </Box>
        
        {/* Show more comments button */}
        {hiddenCount > 0 && !showAllComments && (
          <Box sx={{ mb: 1.5 }}>
            <Button
              startIcon={<ExpandIcon sx={{ fontSize: 16 }} />}
              onClick={() => setShowAllComments(true)}
              size="small"
              sx={{ 
                textTransform: 'none',
                fontSize: '0.75rem',
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  color: theme.palette.primary.main,
                }
              }}
            >
              Show {hiddenCount} earlier {hiddenCount === 1 ? 'comment' : 'comments'}
            </Button>
          </Box>
        )}
        
        {/* Comments List */}
        {visibleComments.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
            {visibleComments.map((commentItem, index) => (
              <Fade in key={commentItem._id || index} timeout={200 * (index + 1)}>
                <Box
                  onMouseEnter={() => setHoveredComment(commentItem._id)}
                  onMouseLeave={() => setHoveredComment(null)}
                  sx={{
                    display: 'flex',
                    p: 1,
                    py: 0.75,
                    bgcolor: (commentItem.isInternalNote || commentItem.isInternal)
                      ? alpha(theme.palette.warning.main, 0.04)
                      : theme.palette.background.paper,
                    borderRadius: 0.5,
                    border: `1px solid ${
                      (commentItem.isInternalNote || commentItem.isInternal)
                        ? alpha(theme.palette.warning.main, 0.2)
                        : alpha(theme.palette.divider, 0.08)
                    }`,
                    borderLeft: (commentItem.isInternalNote || commentItem.isInternal)
                      ? `3px solid ${alpha(theme.palette.warning.main, 0.6)}`
                      : `3px solid transparent`,
                    transition: 'all 0.15s ease',
                    position: 'relative',
                    '&:hover': {
                      bgcolor: (commentItem.isInternalNote || commentItem.isInternal)
                        ? alpha(theme.palette.warning.main, 0.08)
                        : alpha(theme.palette.action.hover, 0.08),
                      borderColor: (commentItem.isInternalNote || commentItem.isInternal)
                        ? alpha(theme.palette.warning.main, 0.3)
                        : alpha(theme.palette.divider, 0.15),
                    }
                  }}
                >
                  {/* User Avatar */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 600,
                      flexShrink: 0,
                      mr: 1.5,
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    {getInitials(commentItem.user) || '?'}
                  </Box>
                  
                  {/* Comment Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Header with user info */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            color: theme.palette.text.primary
                          }}
                        >
                          {getUserName(commentItem.user)}
                        </Typography>
                        
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: theme.palette.text.secondary,
                            fontSize: '0.7rem'
                          }}
                        >
                          {formatTime(commentItem.createdAt)}
                        </Typography>
                      </Box>
                      
                      {(commentItem.isInternalNote || commentItem.isInternal) && (
                        <Chip
                          icon={<PrivateIcon sx={{ fontSize: 10 }} />}
                          label="Team Only"
                          size="small"
                          sx={{ 
                            height: 16,
                            fontSize: '0.6rem !important',
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            color: theme.palette.warning.main,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            '& .MuiChip-label': {
                              px: 0.5,
                              fontSize: '0.6rem !important'
                            },
                            '& .MuiChip-icon': { 
                              ml: 0.25,
                              fontSize: '10px !important',
                              color: theme.palette.warning.main
                            },
                          }}
                        />
                      )}
                    </Box>
                    
                    {/* Comment Text */}
                    <MentionText 
                      text={commentItem.text} 
                      variant="body2" 
                      sx={{ 
                        wordBreak: 'break-word',
                        fontSize: '0.875rem',
                        lineHeight: 1.4
                      }}
                    />
                  </Box>
                </Box>
              </Fade>
            ))}
          </Box>
        )}
        
        {/* Empty state message */}
        {comments.length === 0 && (
          <Box sx={{ 
            textAlign: 'center',
            py: 1,
            mb: 1.5,
            color: 'text.secondary'
          }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
              No comments yet
            </Typography>
          </Box>
        )}
        
        {/* Comment Input - Always visible */}
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 0.5,
              bgcolor: isFocused 
                ? alpha(theme.palette.primary.main, 0.02)
                : alpha(theme.palette.action.hover, 0.02),
              border: `1px solid ${
                isFocused 
                  ? alpha(theme.palette.primary.main, 0.3)
                  : alpha(theme.palette.divider, 0.1)
              }`,
              transition: 'all 0.2s ease',
            }}
            onKeyDown={handleKeyDown}
          >
            <InlineMentionInput
              ref={commentInputRef}
              value={comment}
              onChange={(newValue) => {
                setComment(newValue);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onMentionAdd={(user) => {
                console.log('User mentioned:', user);
              }}
              onMentionRemove={(userId) => {
                console.log('Mention removed:', userId);
              }}
              placeholder="Add a comment... Use @ to mention team members"
              inputProps={{
                style: { 
                  minHeight: '60px',
                  maxHeight: '120px',
                  padding: '8px',
                  paddingRight: '110px', // Space for buttons
                  fontSize: '14px',
                  lineHeight: '1.6',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                },
                'data-placeholder': 'Add a comment... Use @ to mention team members',
              }}
            />
            
            {/* Action Buttons */}
            <Box sx={{ 
              position: 'absolute',
              right: 12,
              bottom: 12,
              display: 'flex',
              gap: 0.5,
            }}>
              {/* Internal note toggle */}
              <IconButton
                size="small"
                onClick={() => setIsInternalNote(!isInternalNote)}
                sx={{
                  p: 0.5,
                  color: isInternalNote ? theme.palette.warning.main : theme.palette.text.secondary,
                  bgcolor: isInternalNote 
                    ? alpha(theme.palette.warning.main, 0.08)
                    : alpha(theme.palette.action.hover, 0.08),
                  '&:hover': {
                    bgcolor: isInternalNote
                      ? alpha(theme.palette.warning.main, 0.15)
                      : alpha(theme.palette.action.hover, 0.15),
                  }
                }}
                title={isInternalNote ? "Remove internal note" : "Make internal note"}
              >
                <PrivateIcon sx={{ fontSize: 16 }} />
              </IconButton>
              
              {/* Send button */}
              <IconButton
                size="small"
                onClick={handleSubmit}
                disabled={!comment.trim() || isSubmitting}
                sx={{
                  p: 0.5,
                  color: (!comment.trim() || isSubmitting) 
                    ? theme.palette.text.disabled 
                    : theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  },
                  '&:disabled': {
                    bgcolor: alpha(theme.palette.action.disabled, 0.08),
                  }
                }}
                title="Send comment"
              >
                <SendIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
          
          {/* Internal note indicator */}
          {isInternalNote && (
            <Fade in>
              <Typography
                variant="caption"
                sx={{ 
                  mt: 0.75,
                  display: 'block',
                  color: theme.palette.warning.main,
                  fontSize: '0.7rem'
                }}
              >
                This comment will be visible to team members only
              </Typography>
            </Fade>
          )}
          
          {/* Helper text when focused */}
          {isFocused && !isInternalNote && (
            <Fade in>
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  mt: 0.75,
                  color: theme.palette.text.secondary,
                  fontSize: '0.7rem'
                }}
              >
                Press @ to mention • {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to send
              </Typography>
            </Fade>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MinimalCommentsEnhanced;